import { LoadingOutlined } from "@ant-design/icons";
import { notification, Spin } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import shortUUID from "short-uuid";

import BulkDownloadPopup from "../../components/common/bulkdownload";
import Header from "../../components/common/Header/container";
import { allUserRoles, ENTITY_TYPE, ROLE, userRoleType } from "../../constants/phase";
import profileFeatureContext from "../../context/profile-access";
import { SocketContext } from "../../context/socket";
import { getEntityType } from "../../helpers";
import { getMyProfile } from "../../store/user/actions";
import { setUserProperties } from "../../utils";
import { isUserAuthenticated } from "../../utils/Storage";

const antIcon = <LoadingOutlined style={{ fontSize: 30, color: "#000", align: "center" }} spin />;

const PrivateRoute = (
  {
    fetchMe,
    getClientSetting,
    getStreamStates,
    getOrganizationProcessingLimit,
    fetchAccessControl,
    getPresignedUrl,
    getDownloadStatus,
    getOrganizationsByIds,
    getFeatureList,
    getUserOrganizations,
    getUserWorkspaces,
    getMyProfile,
    getAllCategory,
    getDownloadVideoStatus,
    getDownloadVideo,
    getCloudPreSignedURL,
  },
  props,
) => {
  const { webhookSocket } = useContext(SocketContext);
  const [eventId, setEventId] = useState("");
  const [type, setType] = useState("sports_keymoments");
  const isAuthenticated = isUserAuthenticated();
  const [lodar, setLodar] = useState(true);
  const [user, setUser] = useState(null);
  const [clientSetting, setClientSetting] = useState({});
  const [parentClientSetting, setParentClientSetting] = useState({});
  const [streamStates, setStreamStates] = useState(0);
  const [totalProcessingLimit, setTotalProcessingLimit] = useState(0);
  const [videosCount, setVideosCount] = useState(0);
  const [streamProcessCount, setStreamProcessCount] = useState(0);
  const [profileFeatures, setProfileFeatures] = useState([]);
  const userId = localStorage.getItem("__uid");
  const [bulkDownloadZip, setBulkDownloadZip] = useState({ downloadStatus: false, folderArray: [], eventId });
  const [downloadStats, setDownloadStats] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [entityType, setEntityType] = useState(ENTITY_TYPE.ORGANIZATION);
  const [clientPermissions, setClientPermissions] = useState([]);
  const [clientPermission, setClientPermission] = useState({});
  const [entityId, setEntityId] = useState("");
  const [currentRole, setCurrentRole] = useState("");
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [autoFlipCategories, setAutoFlipCategories] = useState([]);
  const [entityChangeLoader, setEntityChangeLoader] = useState(false);
  const [isAccountRestrictionEnabled, setIsAccountRestrictionEnabled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (isAccountRestrictionEnabled && pathname !== "/dashboard") {
      navigate("/dashboard", { replace: true });
    }
  }, [isAccountRestrictionEnabled, pathname, navigate]);

  useEffect(() => {
    setLodar(true);
    if (isAuthenticated) {
      (async () => {
        try {
          const {
            payload: { data },
          } = await getMyProfile({});
          if (!data) {
            throw new Error("Something went wrong!");
          }
          setUser(data);
          setUserProperties(data);
          if (data?.roleType === userRoleType.superAdmin) {
            setCurrentRole(allUserRoles.superAdmin);
            // using 'VideoOS' orgId as etityId is used to fetch settings
            setEntityId("org_8eb1dac0-06be-49f6-9940-05062feb822c");
          } else {
            // role will be decided on the basis of active org / workspace
            // make api calls to fetch users' org and workspaces
            // choose the first org in the response as the active entity
            // set state values for active Org / Workspace
            getEntityInfo(data?.entity);
          }
          //sending null id to fetch all feature list
          const { value } = await getFeatureList({ id: null });
          setClientPermissions(value?.data);
          setLodar(false);
        } catch (error) {
          if (error.statusCode === 401) {
            localStorage.clear();
            setTimeout(() => {
              window.location.pathname = "/login";
            }, 2000);
          }
        }
      })();
      const id = shortUUID().generate(5);
      setEventId(id);
      setBulkDownloadZip({ ...bulkDownloadZip, eventId: id });
    }
  }, []);

  useEffect(() => {
    // if role is not superadmin and use switch the profile, nabvaige it to landing page
    const localEntity = localStorage.getItem("entityId");
    if (entityId && currentRole !== ROLE.SUPER_ADMIN && localEntity !== entityId) {
      localStorage.setItem("entityId", entityId);
      setEntityChangeLoader(true);
      setTimeout(() => {
        // show loader on entity change
        setEntityChangeLoader(false);
      }, 500);
      navigate("/dashboard");
    } else if (entityId) {
      localStorage.setItem("entityId", entityId);
    }
  }, [entityId]);

  useEffect(() => {
    if (clientPermissions) {
      setClientPermission(clientPermissions.reduce((obj, cur) => ({ ...obj, [cur.key]: cur.defaultValue }), {}));
    }
  }, [clientPermissions]);

  const getEntityInfo = (entity) => {
    try {
      const prevEntity = localStorage.getItem("entityId");
      setEntityList(entity);

      if (!prevEntity && entity) {
        // if new user logs in..set first org/workspace as default
        const firstEntity = entity.organizations?.[0] || entity.workspaces?.[0];

        if (firstEntity) {
          setEntityId(firstEntity?.organizationId || firstEntity?.workspaceId);
          setCurrentRole(firstEntity?.role || null);
          setSelectedEntity(firstEntity);
        } else {
          // user is not part of any org/workspace
          throw new Error("Entity not found!");
        }
      } else if (prevEntity && entity) {
        // if user has existing val in localstorage
        setEntityList(entity);
        setEntityId(prevEntity);

        const entityType = getEntityType(prevEntity);
        const entityDetails =
          entityType === ENTITY_TYPE.ORGANIZATION
            ? entity.organizations.find(({ organizationId }) => organizationId === prevEntity)
            : entity.workspaces.find(({ workspaceId }) => workspaceId === prevEntity);

        if (entityDetails) {
          setCurrentRole(entityDetails.role || null);
          setSelectedEntity(entityDetails);
        } else {
          throw new Error("Unauthorized!");
        }
      } else {
        throw new Error("Entity not found!");
      }
    } catch (err) {
      // handle condition for no org here
      setEntityId(null);
    }
  };

  const getOrganizationDetails = async (entityId) => {
    const { value } = await getOrganizationsByIds({
      organizationIds: [entityId],
      includeWorkspaces: true,
    });
    if (value.status) {
      setOrganizations(value?.data?.organizations);
      setWorkspaces(value?.data?.workspaces);
    }
  };

  // To get the count of users stream proccesing Limit set by superAdmin
  const getStreamState = async () => {
    const { value } = await getStreamStates({
      type,
      isProduction: false,
      entityId: entityId,
    });
    if (value.success) {
      setStreamStates(value?.states?.videosProcessCount);
      setTotalProcessingLimit(value.totalProcessingLimit || 1);
      setVideosCount(value?.states?.videosCount);
    }
  };
  // To get the complete status of proccessing streams of the Admin and its users
  const getProcessingCount = async () => {
    const { value } = await getOrganizationProcessingLimit({
      entityId: entityId,
    });
    if (value.success) {
      setStreamProcessCount(value?.streams?.length);
    }
  };

  const getAccessControl = async () => {
    const { value } = await fetchAccessControl(currentRole);
    if (value?.status) {
      setProfileFeatures(value.data?.rolePrivileges);
    }
  };

  const getEntityConfig = async () => {
    let arr = [getClientSetting({ entityId })];
    const [clientSetting, parentClientSetting] = await Promise.all(arr);
    let clientData = clientSetting.value.data;
    if (clientData?.permissions) {
      setIsAccountRestrictionEnabled(clientData?.permissions?.enableAccountRestriction);
      // Iterate over keys in clientPermission
      const clientPerms = clientPermissions?.reduce((obj, cur) => ({ ...obj, [cur.key]: cur.defaultValue }), {});
      Object.keys(clientPerms).forEach((key) => {
        // Check if the key is not present in clientData?.permissions then add the keys
        if (!clientData?.permissions.hasOwnProperty(key)) {
          clientData.permissions[key] = clientPerms[key];
        }
      });
    }
    setClientSetting(clientData);
    setParentClientSetting(clientData);
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      getOrganizationDetails(entityId);
      if (entityId) {
        localStorage.setItem("entityId", entityId);
      }
    }
  }, [user]);

  useEffect(() => {
    if (entityId && user) {
      const entity = getEntityType(entityId);
      getOrganizationDetails(entityId);
      setEntityType(entity);
      getEntityConfig();
      getProcessingCount();
      getStreamState();
      getAccessControl();
      getAutoFlipCategories();
    }
  }, [entityId, user]);

  useEffect(() => {
    if (entityId && user) {
      getEntityConfig();
    }
  }, [clientPermissions]);

  useEffect(() => {
    if (eventId) {
      webhookSocket.on(`publisher_zip-url-${eventId}`, ({ data }) => {
        if (data?.status) {
          handleDownload(data?.payload?.event, data?.payload?.zipURL);
          setBulkDownloadZip({ downloadStatus: false, folderArray: [], eventId });
          setTimeout(() => {
            handleDownloadClose();
          }, 5000);
        }
      });
    }
  }, [eventId, webhookSocket]);

  const handleDownload = (fileName, fileUrl) => {
    notification["success"]({ message: "Zip has been downloaded" });
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.rel = "noopener noreferrer";
    link.click();
  };

  const handleDownloadClose = () => {
    setBulkDownloadZip({ downloadStatus: false, folderArray: [], eventId: "" });
  };

  const getAutoFlipCategories = async () => {
    try {
      const { value } = await getAllCategory({
        entityId: entityId,
      });
      if (value.success) {
        let tempData = [];
        value?.data.map((item) => {
          if (item.autoFlip) {
            tempData.push(item?.name);
          }
        });
        setAutoFlipCategories(tempData);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  return isAuthenticated ? (
    <React.Fragment>
      {lodar && !user && !streamStates && !totalProcessingLimit ? (
        <Spin indicator={antIcon} className="loader__full" />
      ) : (
        <>
          {entityChangeLoader && <Spin indicator={antIcon} className="loader__full" />}
          <profileFeatureContext.Provider value={profileFeatures}>
            <Header
              type={type}
              setType={setType}
              clientSetting={clientSetting}
              parentClientSetting={parentClientSetting}
              role={currentRole}
              entityId={entityId}
              setEntityId={setEntityId}
              entityList={entityList}
              setEntityType={setEntityType}
              entityType={entityType}
              setCurrentRole={setCurrentRole}
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
            />

            <Outlet
              context={{
                type,
                setType,
                user,
                clientSetting,
                streamStates,
                totalProcessingLimit,
                getStreamState,
                streamProcessCount,
                getProcessingCount,
                parentClientSetting,
                profileFeatures,
                bulkDownloadZip,
                eventId,
                setBulkDownloadZip,
                getPresignedUrl,
                getDownloadStatus,
                downloadStats,
                setDownloadStats,
                organizations,
                workspaces,
                entityType,
                entityId,
                currentRole,
                selectedEntity,
                videosCount,
                autoFlipCategories,
                entityList,
                getDownloadVideoStatus,
                getDownloadVideo,
                getCloudPreSignedURL,
              }}
            />
            {bulkDownloadZip?.downloadStatus && (
              <BulkDownloadPopup
                folderArray={bulkDownloadZip?.folderArray}
                downloadStatus={bulkDownloadZip?.downloadStatus}
                handleDownloadClose={handleDownloadClose}
              />
            )}
          </profileFeatureContext.Provider>
        </>
      )}
    </React.Fragment>
  ) : (
    <Navigate to="/login" />
  );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = {
  getMyProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);
